import axios from 'axios';
import { getBackendHost } from '../config/EnvData';
import emailService from './emailService';
import { authService } from './authService';

const BACKEND_API_PREFIX = getBackendHost();

class NotificationService {

  // Authorization header carrying the Cognito ID token. The backend derives the
  // user's identity from this verified token, not from any user_email param.
  async _authHeaders() {
    const token = await authService.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async getCurrentUserEmail() {
    try {
      return await emailService.getCurrentUserEmail();
    } catch (error) {
      console.error('Failed to get current user email:', error);
      return null;
    }
  }

  async getUserNotifications(userEmail = null) {
    try {
      const currentUserEmail = userEmail || await this.getCurrentUserEmail();
      
      if (!currentUserEmail) {
        console.warn('No user email provided for notifications');
        return { success: false, notifications: [] };
      }

      const response = await axios.get(`${BACKEND_API_PREFIX}/api/notifications/`, {
        headers: await this._authHeaders()
      });

      return {
        success: true,
        notifications: response.data.notifications || []
      };
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return { success: false, notifications: [] };
    }
  }

  async markNotificationAsRead(notificationId, userEmail = null) {
    try {
      const currentUserEmail = userEmail || await this.getCurrentUserEmail();
      
      if (!currentUserEmail) {
        console.error('No user email available for marking notification as read');
        return { success: false };
      }

      const response = await axios.post(`${BACKEND_API_PREFIX}/api/notifications/mark-read/`, {
        notification_id: notificationId
      }, {
        headers: await this._authHeaders()
      });

      return { success: response.data.success };
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return { success: false };
    }
  }

  async deleteNotification(notificationId, userEmail = null) {
    try {
      const currentUserEmail = userEmail || await this.getCurrentUserEmail();
      
      if (!currentUserEmail) {
        console.error('No user email available for deleting notification');
        return { success: false };
      }

      const response = await axios.delete(`${BACKEND_API_PREFIX}/api/notifications/delete/`, {
        data: { notification_id: notificationId },
        headers: await this._authHeaders()
      });

      return { success: response.data.success };
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return { success: false };
    }
  }

  async clearAllNotifications(userEmail = null) {
    try {
      const currentUserEmail = userEmail || await this.getCurrentUserEmail();
      
      if (!currentUserEmail) {
        console.error('No user email available for clearing notifications');
        return { success: false };
      }

      const response = await axios.delete(`${BACKEND_API_PREFIX}/api/notifications/clear-all/`, {
        data: {},
        headers: await this._authHeaders()
      });

      return { success: response.data.success, deletedCount: response.data.deleted_count };
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
      return { success: false };
    }
  }

  async createNotification(notificationData) {
    try {
      const currentUserEmail = notificationData.user_email || await this.getCurrentUserEmail();
      
      if (!currentUserEmail) {
        console.error('No user email available for creating notification');
        return { success: false };
      }

      const enrichedNotificationData = {
        ...notificationData,
        user_email: currentUserEmail
      };

      const response = await axios.post(`${BACKEND_API_PREFIX}/api/notifications/create/`, enrichedNotificationData, {
        headers: await this._authHeaders()
      });

      return { success: response.data.success, notificationId: response.data.notification_id };
    } catch (error) {
      console.error('Failed to create notification:', error);
      return { success: false };
    }
  }

  // Open a self-managing Server-Sent Events stream for live notifications.
  // The server sends a `snapshot` (full current list) on connect, then a
  // `notification` per new item. Returns a handle with .close().
  //
  // EventSource can't set headers, so the Cognito ID token is passed as a query
  // param. EventSource's built-in reconnect would reuse the original (possibly
  // expired) token, so we manage reconnection ourselves with exponential backoff
  // and mint a FRESH token on each (re)connect. Non-blocking; safe to call sync.
  openNotificationStream(userEmail, { onSnapshot, onNotification } = {}) {
    if (!userEmail || typeof EventSource === 'undefined') {
      return { close() {} };
    }

    let source = null;
    let stopped = false;
    let retries = 0;
    let retryTimer = null;

    const connect = async () => {
      if (stopped) return;
      const token = await authService.getIdToken();
      if (stopped) return;

      const params = new URLSearchParams({ user_email: userEmail });
      if (token) params.set('token', token);
      source = new EventSource(`${BACKEND_API_PREFIX}/api/notifications/stream/?${params.toString()}`);

      source.addEventListener('snapshot', (event) => {
        retries = 0; // a clean connect resets backoff
        try {
          if (onSnapshot) onSnapshot(JSON.parse(event.data).notifications || []);
        } catch (err) {
          console.error('Failed to parse notification snapshot:', err);
        }
      });

      source.addEventListener('notification', (event) => {
        try {
          if (onNotification) onNotification(JSON.parse(event.data));
        } catch (err) {
          console.error('Failed to parse notification event:', err);
        }
      });

      source.onerror = () => {
        // Take control from EventSource's auto-reconnect so we can refresh the token.
        if (source) source.close();
        if (stopped) return;
        const delay = Math.min(30000, 1000 * 2 ** retries);
        retries += 1;
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return {
      close() {
        stopped = true;
        if (retryTimer) clearTimeout(retryTimer);
        if (source) source.close();
      }
    };
  }

  async getUserPendingJobs(userEmail = null) {
    try {
      const currentUserEmail = userEmail || await this.getCurrentUserEmail();
      
      if (!currentUserEmail) {
        console.warn('No user email provided for pending jobs');
        return { success: false, pendingJobs: [] };
      }

      const response = await axios.get(`${BACKEND_API_PREFIX}/api/get-user-pending-jobs/`, {
        headers: await this._authHeaders()
      });

      return {
        success: true,
        pendingJobs: response.data.pendingJobs || []
      };
    } catch (error) {
      console.error('Failed to fetch pending jobs:', error);
      return { success: false, pendingJobs: [] };
    }
  }

  // Helper method to migrate existing localStorage notifications to backend
  async migrateLocalStorageNotifications(userEmail, localNotifications) {
    if (!userEmail || !localNotifications || localNotifications.length === 0) {
      return { success: true, migrated: 0 };
    }

    let migratedCount = 0;
    for (const notification of localNotifications) {
      try {
        const result = await this.createNotification({
          user_email: userEmail,
          title: notification.text || 'Historic Data Download',
          message: notification.text || 'Your data download is ready',
          notification_type: 'download',
          download_link: notification.downloadLink,
          expires_in_days: 7
        });
        
        if (result.success) {
          migratedCount++;
        }
      } catch (error) {
        console.error('Failed to migrate notification:', error);
      }
    }

    return { success: true, migrated: migratedCount };
  }
}

export const notificationService = new NotificationService();